import React, { useMemo } from "react";

export interface BackgroundGradientAnimationProps {
  gradientBackgroundStart?: string;
  gradientBackgroundEnd?: string;
  firstColor?: string; // e.g. "18, 113, 255"
  secondColor?: string;
  thirdColor?: string;
  fourthColor?: string;
  fifthColor?: string;
  pointerColor?: string;
  size?: string; // e.g. "80%"
  blendingValue?: React.CSSProperties["mixBlendMode"]; // e.g. "hard-light"
  interactive?: boolean;
  children?: React.ReactNode;
  className?: string; // additional classes for content wrapper
  containerClassName?: string; // additional classes for main container
}

export const BackgroundGradientAnimation: React.FC<BackgroundGradientAnimationProps> = ({
  gradientBackgroundStart = "rgb(108, 0, 162)",
  gradientBackgroundEnd = "rgb(0, 17, 82)",
  firstColor = "18, 113, 255",
  secondColor = "221, 74, 255",
  thirdColor = "100, 220, 255",
  fourthColor = "200, 50, 50",
  fifthColor = "180, 180, 50",
  pointerColor = "140, 100, 255",
  size = "80%",
  blendingValue = "hard-light",
  interactive = true,
  children,
  className,
  containerClassName,
}) => {
  const ids = useMemo(() => {
    const base = Math.random().toString(36).slice(2);
    return {
      root: `bga-root-${base}`,
      orb: `bga-orb-${base}`,
      pointer: `bga-pointer-${base}`,
      kfA: `bga-a-${base}`,
      kfB: `bga-b-${base}`,
      kfC: `bga-c-${base}`,
      kfD: `bga-d-${base}`,
      kfE: `bga-e-${base}`,
    };
  }, []);

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${containerClassName || ""}`}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, ${gradientBackgroundStart}, transparent 60%), radial-gradient(120% 100% at 100% 0%, ${gradientBackgroundEnd}, transparent 60%), linear-gradient(180deg, ${gradientBackgroundStart}, ${gradientBackgroundEnd})`,
      }}
    >
      <style>
        {`
          @keyframes ${ids.kfA} { 0% { transform: translate(-10%, -10%) scale(1); } 25% { transform: translate(20%, -20%) scale(1.1); } 50% { transform: translate(30%, 10%) scale(0.95); } 75% { transform: translate(-20%, 20%) scale(1.05); } 100% { transform: translate(-10%, -10%) scale(1); } }
          @keyframes ${ids.kfB} { 0% { transform: translate(60%, -20%) scale(1.1); } 25% { transform: translate(40%, 10%) scale(0.9); } 50% { transform: translate(70%, 30%) scale(1.05); } 75% { transform: translate(80%, -10%) scale(1); } 100% { transform: translate(60%, -20%) scale(1.1); } }
          @keyframes ${ids.kfC} { 0% { transform: translate(10%, 60%) scale(0.9); } 25% { transform: translate(-10%, 40%) scale(1.05); } 50% { transform: translate(20%, 70%) scale(1); } 75% { transform: translate(0%, 50%) scale(0.95); } 100% { transform: translate(10%, 60%) scale(0.9); } }
          @keyframes ${ids.kfD} { 0% { transform: translate(80%, 60%) scale(1); } 25% { transform: translate(60%, 80%) scale(0.95); } 50% { transform: translate(90%, 50%) scale(1.1); } 75% { transform: translate(70%, 70%) scale(1.05); } 100% { transform: translate(80%, 60%) scale(1); } }
          @keyframes ${ids.kfE} { 0% { transform: translate(-20%, 50%) scale(1); } 25% { transform: translate(0%, 30%) scale(1.05); } 50% { transform: translate(10%, 80%) scale(0.95); } 75% { transform: translate(-10%, 60%) scale(1.1); } 100% { transform: translate(-20%, 50%) scale(1); } }

          .${ids.orb} {
            position: absolute;
            width: ${size};
            height: ${size};
            border-radius: 50%;
            filter: blur(40px) saturate(150%);
            opacity: 0.85;
            mix-blend-mode: ${blendingValue};
            will-change: transform;
          }
          .${ids.pointer} {
            position: absolute;
            width: calc(${size} * 0.35);
            height: calc(${size} * 0.35);
            border-radius: 50%;
            filter: blur(30px) saturate(160%);
            opacity: 0.9;
            mix-blend-mode: ${blendingValue};
            transform: translate(-50%, -50%);
            pointer-events: none;
            transition: transform 200ms ease;
          }
        `}
      </style>

      <div
        className={ids.orb}
        style={{
          background: `radial-gradient(circle at 30% 30%, rgba(${firstColor}, 0.9), rgba(${firstColor}, 0.15) 60%)`,
          animation: `${ids.kfA} 28s ease-in-out infinite`,
          left: "-10%",
          top: "-10%",
        }}
      />
      <div
        className={ids.orb}
        style={{
          background: `radial-gradient(circle at 60% 40%, rgba(${secondColor}, 0.85), rgba(${secondColor}, 0.15) 60%)`,
          animation: `${ids.kfB} 34s ease-in-out infinite`,
          right: "-10%",
          top: "-15%",
        }}
      />
      <div
        className={ids.orb}
        style={{
          background: `radial-gradient(circle at 40% 60%, rgba(${thirdColor}, 0.85), rgba(${thirdColor}, 0.15) 60%)`,
          animation: `${ids.kfC} 30s ease-in-out infinite`,
          left: "-5%",
          bottom: "-10%",
        }}
      />
      <div
        className={ids.orb}
        style={{
          background: `radial-gradient(circle at 60% 60%, rgba(${fourthColor}, 0.85), rgba(${fourthColor}, 0.15) 60%)`,
          animation: `${ids.kfD} 38s ease-in-out infinite`,
          right: "-10%",
          bottom: "-5%",
        }}
      />
      <div
        className={ids.orb}
        style={{
          background: `radial-gradient(circle at 50% 50%, rgba(${fifthColor}, 0.85), rgba(${fifthColor}, 0.15) 60%)`,
          animation: `${ids.kfE} 32s ease-in-out infinite`,
          left: "10%",
          top: "40%",
        }}
      />

      {interactive && (
        <MouseFollower color={pointerColor} className={ids.pointer} />
      )}

      <div className={className || "z-50"}>{children}</div>
    </div>
  );
};

const MouseFollower: React.FC<{ color: string; className: string }> = ({ color, className }) => {
  const [pos, setPos] = React.useState<{ x: number; y: number }>({ x: -9999, y: -9999 });

  React.useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      setPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  return (
    <div
      className={className}
      style={{
        left: pos.x,
        top: pos.y,
        background: `radial-gradient(circle at 50% 50%, rgba(${color}, 0.95), rgba(${color}, 0.2) 60%)`,
      }}
    />
  );
};

export default BackgroundGradientAnimation;
